import React, { useState } from 'react';
import { THEME_SLUGS, themeLabel } from './themes';

// Barre de filtres présentée comme UNE PHRASE (propre et concise), reprenant le
// titre de l'ancien site et y intégrant les DEUX filtres en ligne :
//   « Apprenez les [500 mots] les plus fréquents en russe · [Quiz complet] »
// Les deux crochets sont des sélecteurs (fréquence + thème) : on tape, un tiroir
// s'ouvre en grille, on choisit, il se referme. Les deux axes se CROISENT (thème ∩
// bande de fréquence) côté données. Le × d'un thème actif revient à « tous ».

const Chevron = () => (
  <svg viewBox="0 0 20 20" className="w-3 h-3 fill-current opacity-60 shrink-0" aria-hidden="true">
    <path d="M5.5 7.5 10 12l4.5-4.5H5.5z" />
  </svg>
);

// Les sélecteurs sont des MOTS DE LA PHRASE, pas des boutons posés à côté.
// Ils étaient stylés en pastilles (fond, bordure, arrondi complet, gras) : une
// pastille se lit comme un objet EXTÉRIEUR au texte, donc l'œil butait deux fois
// (« Apprenez les » | pavé | « les plus fréquents en russe » | pavé). Pire, le
// poids était inversé : la phrase en gris clair, les pastilles en blanc gras sur
// fond clair. Ce qui criait, c'était l'emballage. Sur téléphone la phrase passait
// à la ligne au milieu et devenait illisible.
// Ici : même taille et même ligne de base que la phrase, en bleu souligné, comme
// un lien. Le soulignement pointillé dit « ça s'ouvre » sans sortir du texte.
const mot = (state) =>
  'inline-flex items-baseline gap-1 font-semibold underline decoration-dotted underline-offset-4 ' +
  'transition-colors rounded-sm px-0.5 -mx-0.5 ' +
  (state === 'open'
    ? 'text-blue-200 decoration-blue-300'
    : 'text-blue-300 decoration-blue-400/60 hover:text-blue-200 hover:decoration-blue-300');

const drawerChip = (active) =>
  'px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ' +
  (active
    ? 'bg-blue-600 text-white border-blue-500'
    : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-blue-500 hover:text-white');

export default function FilterBar({
  frequencies,
  frequency,
  onPickFrequency,
  theme,
  onPickTheme,
  apiLang,
  allLabel,
  titlePrefix,
  titleSuffix,
}) {
  const [open, setOpen] = useState(null); // 'freq' | 'theme' | null
  const toggle = (which) => setOpen((o) => (o === which ? null : which));
  const pickFreq = (v) => { onPickFrequency(v); setOpen(null); };
  const pickTheme = (slug) => { onPickTheme(slug); setOpen(null); };

  const activeFreq = frequencies.find((f) => f.value === frequency) || frequencies[0];

  return (
    <div className="flex flex-col gap-2">
      {/* UNE phrase, centrée, avec ses deux filtres dedans. Elle se lit d'une
          traite, y compris quand elle passe à la ligne : les sélecteurs sont des
          mots, donc une coupure au milieu reste une phrase. */}
      {/* `text-base` et pas `md:text-lg` : à 20,25 px la phrase demande 747 px pour
          720 disponibles dans le conteneur, donc elle passait sur deux lignes dès
          qu'un thème long était choisi. À 18 px elle tient sur une ligne, même avec
          le libellé de thème le plus long. Sur téléphone elle se plie, c'est
          inévitable et ça reste une phrase. */}
      <p className="text-center text-base text-slate-300 leading-relaxed">
        {titlePrefix}{' '}
        <button onClick={() => toggle('freq')} className={mot(open === 'freq' ? 'open' : 'idle')}>
          {activeFreq.label}
          <Chevron />
        </button>{' '}
        {titleSuffix}
        {theme ? (
          /* `whitespace-nowrap` : le × est un élément à part, donc il pouvait
             passer SEUL à la ligne suivante et se retrouver orphelin sous la
             phrase (reproduit à partir de 770 px avec un thème long). Le thème et
             sa croix forment un bloc insécable : ils vont à la ligne ensemble. */
          <span className="whitespace-nowrap">
            {' · '}
            <button onClick={() => toggle('theme')} className={mot(open === 'theme' ? 'open' : 'idle')}>
              {themeLabel(theme, apiLang)}
              <Chevron />
            </button>
            <button
              onClick={() => pickTheme(null)}
              title={allLabel}
              aria-label={allLabel}
              className="inline-flex items-center justify-center w-[30px] h-[30px] align-middle text-slate-500 hover:text-slate-200 transition-colors"
            >
              ×
            </button>
          </span>
        ) : (
          <>
            {' · '}
            <button onClick={() => toggle('theme')} className={mot(open === 'theme' ? 'open' : 'idle')}>
              {allLabel}
              <Chevron />
            </button>
          </>
        )}
      </p>

      {/* Tiroirs de sélection */}
      {open === 'freq' && (
        <div className="flex flex-wrap gap-2 bg-slate-800/50 border border-slate-700/50 rounded-xl p-3">
          {frequencies.map((f) => (
            <button key={f.value} onClick={() => pickFreq(f.value)} className={drawerChip(f.value === frequency)}>
              {f.label}
            </button>
          ))}
        </div>
      )}
      {open === 'theme' && (
        <div className="flex flex-wrap gap-2 bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 max-h-60 overflow-y-auto">
          <button onClick={() => pickTheme(null)} className={drawerChip(!theme)}>
            {allLabel}
          </button>
          {THEME_SLUGS.map((slug) => (
            <button key={slug} onClick={() => pickTheme(slug)} className={drawerChip(slug === theme)}>
              {themeLabel(slug, apiLang)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
